// contexts/SnackbarContext.js
import React, { createContext, useContext, useState } from "react";
import { Snackbar, Alert } from "@mui/material";

const SnackbarContext = createContext();

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

export const SnackbarProvider = ({ children }) => {
  const [snackbars, setSnackbars] = useState([]);

  const showSnackbar = (message, severity = "info", duration = 4000) => {
    const id = Date.now() + Math.random(); // Simple unique ID
    const newSnackbar = {
      id,
      message,
      severity,
      duration,
      open: true,
    };

    setSnackbars((prev) => [...prev, newSnackbar]);

    // Auto-hide after duration
    setTimeout(() => {
      hideSnackbar(id);
    }, duration);
  };

  // Specific methods for different types
  const showSuccess = (message, duration = 4000) => {
    showSnackbar(message, "success", duration);
  };

  const showError = (message, duration = 6000) => {
    showSnackbar(message, "error", duration);
  };

  const showInfo = (message, duration = 4000) => {
    showSnackbar(message, "info", duration);
  };

  const showWarning = (message, duration = 5000) => {
    showSnackbar(message, "warning", duration);
  };

  const hideSnackbar = (id) => {
    setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id));
  };

  return (
    <SnackbarContext.Provider
      value={{
        showSnackbar,
        showSuccess,
        showError,
        showInfo,
        showWarning,
      }}
    >
      {children}

      {/* Render all active snackbars */}
      {snackbars.map((snackbar, index) => (
        <Snackbar
          key={snackbar.id}
          open={snackbar.open}
          autoHideDuration={snackbar.duration}
          onClose={() => hideSnackbar(snackbar.id)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          sx={{
            // Stack multiple snackbars
            bottom: `${80 + index * 60}px !important`,
          }}
        >
          <Alert
            onClose={() => hideSnackbar(snackbar.id)}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      ))}
    </SnackbarContext.Provider>
  );
};
